/**
 * GitHub Authentication via OAuth and Personal Access Token
 * Supports both OAuth sign-in flow and PAT fallback
 */

const USER_URL = 'https://api.github.com/user';
const REPOS_URL = 'https://api.github.com/user/repos';

// GitHub OAuth configuration - Client ID is public, secret stays on server
const GITHUB_CLIENT_ID = import.meta.env.VITE_GITHUB_CLIENT_ID || '';
const OAUTH_REDIRECT_URI = typeof window !== 'undefined'
  ? `${window.location.origin}/`
  : '';

/**
 * Start GitHub OAuth flow - redirects to GitHub for authorization
 */
export function startOAuthFlow(): void {
  if (!GITHUB_CLIENT_ID) {
    throw new Error('GitHub OAuth is not configured');
  }

  // Generate random state for CSRF protection
  const state = crypto.randomUUID();
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id: GITHUB_CLIENT_ID,
    redirect_uri: OAUTH_REDIRECT_URI,
    scope: 'repo read:user',
    state,
  });

  window.location.href = `https://github.com/login/oauth/authorize?${params}`;
}

/**
 * Handle OAuth callback - exchange code for token
 */
export async function handleOAuthCallback(code: string, state: string): Promise<{ token: string; user: GitHubUser }> {
  // Verify state to prevent CSRF
  const savedState = sessionStorage.getItem('oauth_state');
  sessionStorage.removeItem('oauth_state');

  if (!savedState || savedState !== state) {
    throw new Error('Invalid OAuth state - please try signing in again');
  }

  // Exchange code for token via our API
  const response = await fetch('/api/auth/github', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to authenticate with GitHub');
  }

  const { access_token } = await response.json();

  // Get user info and store token
  const user = await getCurrentUser(access_token);
  storeToken(access_token);

  return { token: access_token, user };
}

/**
 * Check if current URL has OAuth callback parameters
 */
export function hasOAuthCallback(): { code: string; state: string } | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');

  if (code && state) {
    return { code, state };
  }
  return null;
}

/**
 * Clear OAuth callback params from URL
 */
export function clearOAuthParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, '', url.pathname);
}

/**
 * Check if OAuth is configured (client ID available)
 */
export function isOAuthConfigured(): boolean {
  return !!GITHUB_CLIENT_ID;
}

export interface GitHubUser {
  login: string;
  id: number;
  avatar_url: string;
  name: string | null;
  email: string | null;
}

export interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  description: string | null;
  html_url: string;
  clone_url: string;
  default_branch: string;
  updated_at: string;
  pushed_at: string;
  stargazers_count: number;
  language: string | null;
  owner: {
    login: string;
    avatar_url: string;
  };
}

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: GitHubUser | null;
}

const STORAGE_KEY = 'webwebweb_github_token';

/**
 * Get stored auth token from localStorage
 */
export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store auth token in localStorage
 */
export function storeToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    console.warn('Failed to store token in localStorage');
  }
}

/**
 * Clear stored auth token
 */
export function clearToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    console.warn('Failed to clear token from localStorage');
  }
}

/**
 * Get current user info
 */
export async function getCurrentUser(token: string): Promise<GitHubUser> {
  const response = await fetch(USER_URL, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github+json'
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Invalid token. Please check your Personal Access Token.');
    }
    throw new Error(`Failed to get user: ${response.status}`);
  }

  return response.json();
}

/**
 * Validate token by checking if we can access the user endpoint
 */
export async function validateToken(token: string): Promise<boolean> {
  try {
    await getCurrentUser(token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get user's repositories
 */
export async function getUserRepos(
  token: string,
  options: {
    page?: number;
    perPage?: number;
    sort?: 'updated' | 'pushed' | 'full_name';
    type?: 'all' | 'owner' | 'public' | 'private';
  } = {}
): Promise<GitHubRepo[]> {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    per_page: String(options.perPage || 30),
    sort: options.sort || 'updated',
    type: options.type || 'all'
  });

  const response = await fetch(`${REPOS_URL}?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/vnd.github+json'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to get repos: ${response.status}`);
  }

  return response.json();
}

/**
 * Search repositories
 */
export async function searchRepos(
  token: string,
  query: string
): Promise<GitHubRepo[]> {
  const response = await fetch(
    `https://api.github.com/search/repositories?q=${encodeURIComponent(query)}&per_page=20`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/vnd.github+json'
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to search repos: ${response.status}`);
  }

  const data = await response.json();
  return data.items;
}

/**
 * Authenticate with a Personal Access Token
 */
export async function authenticateWithToken(token: string): Promise<{ token: string; user: GitHubUser }> {
  const user = await getCurrentUser(token);
  storeToken(token);
  return { token, user };
}

/**
 * Initialize auth state from stored token
 */
export async function initializeAuth(): Promise<AuthState> {
  const token = getStoredToken();

  if (!token) {
    return { isAuthenticated: false, token: null, user: null };
  }

  // Validate token
  try {
    const user = await getCurrentUser(token);
    return { isAuthenticated: true, token, user };
  } catch {
    // Token is invalid, clear it
    clearToken();
    return { isAuthenticated: false, token: null, user: null };
  }
}

/**
 * Sign out - clear stored credentials
 */
export function signOut(): void {
  clearToken();
}
